import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import type { FilePickerCapabilities } from "../../../api/ReactCoreClient";
import type { ReactComponentContext } from "../../Contexts/ReactRuntimeBridge";

export type FileReference = Readonly<{ fileId: string; name: string; contentType?: string; size?: number; url?: string; urlExpiresAt?: string; publicUrl?: string }>;
export type FilePickerDialogProps = Readonly<{
  ctx: ReactComponentContext;
  open: boolean;
  mode?: "upload" | "select" | "upload-or-select";
  multiple?: boolean;
  accept?: readonly string[];
  maximumFileBytes?: number;
  maximumFiles?: number;
  selectedFileIds?: readonly string[];
  attributes?: Readonly<Record<string, string>>;
  publicOnly?: boolean;
  allowPublicUpload?: boolean;
  title?: string;
  onConfirm: (files: readonly FileReference[]) => void;
  onClose: () => void;
}>;

export function acceptsFile(contentType: string | undefined, accepted: readonly string[]) {
  return (
    !accepted.length ||
    accepted.some(
      (pattern) =>
        pattern === "*/*" ||
        pattern.toLowerCase() === contentType?.toLowerCase() ||
        (pattern.endsWith("/*") && contentType?.toLowerCase().startsWith(pattern.slice(0, -1).toLowerCase()))
    )
  );
}

function reference(file: { id: string; name?: string; contentType?: string; size?: number; url?: string; publicUrl?: string }): FileReference {
  let urlExpiresAt: string | undefined;
  if (file.url) {
    try {
      const expires = new URL(file.url).searchParams.get("se");
      if (expires && Number.isFinite(Date.parse(expires))) urlExpiresAt = new Date(expires).toISOString();
    } catch {
      /* No expiry can be inferred for an opaque URL. */
    }
  }
  return { fileId: file.id, name: file.name ?? file.id, contentType: file.contentType, size: file.size, url: file.url || undefined, urlExpiresAt, publicUrl: file.publicUrl };
}

/** Native host UI. File identity is durable; signed URLs are temporary and never saved by this dialog. */
export default function FilePickerDialog({
  ctx,
  open,
  mode = "upload-or-select",
  multiple = false,
  accept = [],
  maximumFileBytes,
  maximumFiles = 10,
  selectedFileIds = [],
  attributes,
  publicOnly = false,
  allowPublicUpload = false,
  title = "Choose files",
  onConfirm,
  onClose,
}: FilePickerDialogProps) {
  const files = ctx.core.files;
  const [capabilities, setCapabilities] = useState<FilePickerCapabilities>();
  const [items, setItems] = useState<FileReference[]>([]);
  const [selected, setSelected] = useState<FileReference[]>([]);
  const [unresolved, setUnresolved] = useState<readonly string[]>([]);
  const [page, setPage] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState("");
  const [fullScreen, setFullScreen] = useState(false);
  const [preview, setPreview] = useState<FileReference>();
  const [retry, setRetry] = useState(0);
  const [permissionRetry, setPermissionRetry] = useState(0);
  const [showPublicOnly, setShowPublicOnly] = useState(false);
  const [publishPublicly, setPublishPublicly] = useState(false);
  const lifetime = useRef<AbortController>();
  const input = useRef<HTMLInputElement>(null);
  const initialIds = JSON.stringify(selectedFileIds);
  const filterKey = JSON.stringify(attributes ?? {});
  const limit = multiple ? Math.max(1, Math.min(100, Math.floor(maximumFiles) || 10)) : 1;
  const canSelect = capabilities?.canSelect && mode !== "upload";
  const canUpload = capabilities?.canUpload && mode !== "select";
  const canPublish = canUpload && allowPublicUpload && capabilities?.canPublishPublicly;
  const filterPublic = publicOnly || showPublicOnly;
  const permitted = (file: FileReference) => acceptsFile(file.contentType, accept) && acceptsFile(file.contentType, capabilities?.acceptedContentTypes ?? []);

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    lifetime.current = controller;
    setCapabilities(undefined);
    setItems([]);
    setPage(0);
    setError("");
    setBusy(false);
    setUploading("");
    setPreview(undefined);
    setShowPublicOnly(false);
    setPublishPublicly(false);
    const ids = Array.from(new Set(JSON.parse(initialIds) as string[]));
    setSelected(ids.map((fileId) => ({ fileId, name: fileId })));
    setUnresolved(ids);
    void files
      .capabilities({ signal: controller.signal })
      .then(async (value) => {
        if (controller.signal.aborted) return;
        setCapabilities(value);
        if (!ids.length) return;
        if (ids.length > 100) throw new Error("Too many saved files. Remove selections explicitly before choosing replacements.");
        if (!value.canSelect) throw new Error("Existing files cannot be read with this host's permissions. Remove them explicitly to choose replacements.");
        const hydrated: FileReference[] = [];
        for (const id of ids) hydrated.push(reference(await files.getById(id, { signal: controller.signal })));
        if (!controller.signal.aborted) {
          setSelected((current) => current.map((item) => hydrated.find((file) => file.fileId === item.fileId) ?? item));
          setUnresolved((current) => current.filter((id) => !hydrated.some((file) => file.fileId === id)));
        }
      })
      .catch((failure: unknown) => {
        if (!controller.signal.aborted) setError(failure instanceof Error ? failure.message : "Files could not be loaded.");
      });
    return () => controller.abort();
  }, [open, files, initialIds, limit, permissionRetry]);

  useEffect(() => {
    if (!open || !canSelect || (filterPublic && !capabilities?.canFilterPublic)) return;
    const controller = new AbortController();
    setLoading(true);
    void files
      .search({ attributes: JSON.parse(filterKey), pageIndex: page, pageSize: 20, ...(filterPublic ? { publicOnly: true } : {}) }, { signal: controller.signal })
      .then((result) => {
        if (!controller.signal.aborted) {
          setItems(result.items.map(reference));
          setHasNext(result.hasNextPage);
        }
      })
      .catch((failure: unknown) => {
        if (!controller.signal.aborted) setError(failure instanceof Error ? failure.message : "Files could not be loaded.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [open, files, canSelect, page, filterKey, retry, filterPublic, capabilities?.canFilterPublic]);

  const close = () => {
    lifetime.current?.abort();
    setPreview(undefined);
    onClose();
  };
  const showPreview = async (file: FileReference) => {
    const signal = lifetime.current?.signal;
    try {
      const current = reference(await files.getById(file.fileId, { signal }));
      if (signal?.aborted) return;
      const url = new URL(current.url ?? "");
      if (url.protocol !== "https:" && !(url.protocol === "http:" && ["localhost", "127.0.0.1"].includes(url.hostname)))
        throw new Error("This file has no safe preview URL.");
      setPreview(current);
    } catch (failure) {
      if (!signal?.aborted) setError(failure instanceof Error ? failure.message : "Preview is unavailable.");
    }
  };
  const toggle = (file: FileReference) => {
    setUnresolved((ids) => ids.filter((id) => id !== file.fileId));
    setSelected((current) =>
      current.some((item) => item.fileId === file.fileId)
        ? current.filter((item) => item.fileId !== file.fileId)
        : multiple
          ? [...current, file].slice(0, limit)
          : [file]
    );
  };
  const upload = async (chosen: File[]) => {
    const signal = lifetime.current?.signal;
    if (!signal || signal.aborted || !capabilities || busy) return;
    setBusy(true);
    setError("");
    const makePublic = !!canPublish && publishPublicly;
    setPublishPublicly(false);
    try {
      for (const file of chosen.slice(0, limit - (multiple ? selected.length : 0))) {
        if (signal.aborted) return;
        const maximum = Math.min(capabilities.maximumFileBytes, maximumFileBytes ?? capabilities.maximumFileBytes, makePublic ? 10 * 1024 * 1024 : Infinity);
        if (file.size <= 0 || file.size > maximum || !permitted({ fileId: "", name: file.name, contentType: file.type })) {
          throw new Error(`${file.name}: this file exceeds the permitted size or type.`);
        }
        setUploading(file.name);
        const uploaded = await files.upload(file, { signal, ...(makePublic ? { publishPublicly: true } : {}) });
        if (signal.aborted) return;
        // Do not lose a successful upload if URL/metadata hydration fails after storage committed.
        let result: FileReference = { fileId: uploaded.id, name: file.name, contentType: file.type, size: file.size, url: uploaded.publicUrl, publicUrl: uploaded.publicUrl };
        if (capabilities.canSelect) {
          try {
            const stored = reference(await files.getById(uploaded.id, { signal }));
            result = { ...stored, publicUrl: stored.publicUrl ?? result.publicUrl };
          } catch {
            if (signal.aborted) return;
          }
        }
        if (!signal.aborted)
          setSelected((current) => (multiple ? [...current.filter((item) => item.fileId !== result.fileId), result].slice(0, limit) : [result]));
      }
    } catch (failure) {
      if (!signal.aborted)
        setError(failure instanceof Error ? failure.message : "Upload failed. Check the file list before retrying; the file may have been stored.");
    } finally {
      if (!signal.aborted) {
        setBusy(false);
        setUploading("");
      }
    }
  };
  const confirm = async () => {
    const signal = lifetime.current?.signal;
    if (!signal || signal.aborted) return;
    setBusy(true);
    setError("");
    try {
      const result: FileReference[] = [];
      for (const file of selected) {
        const refreshed = capabilities?.canSelect ? reference(await files.getById(file.fileId, { signal })) : file;
        const current = { ...refreshed, publicUrl: refreshed.publicUrl ?? file.publicUrl };
        if (!permitted(current)) throw new Error(`${current.name}: this file type is not permitted.`);
        if (publicOnly && !current.publicUrl) throw new Error(`${current.name}: choose a public file.`);
        result.push(current);
      }
      if (!signal.aborted) onConfirm(Object.freeze(result));
    } catch (failure) {
      if (!signal.aborted) setError(failure instanceof Error ? failure.message : "Selection could not be confirmed.");
    } finally {
      if (!signal.aborted) setBusy(false);
    }
  };
  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="md" fullScreen={fullScreen} disableEnforceFocus={!!preview}>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          {title}
          <Box>
            <IconButton aria-label={fullScreen ? "Exit fullscreen" : "Fullscreen"} onClick={() => setFullScreen(!fullScreen)}>
              {fullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
            <IconButton aria-label="Close file picker" onClick={close}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error && (
            <Alert
              severity="error"
              action={
                <Button
                  disabled={busy}
                  onClick={() => {
                    setError("");
                    if (!capabilities) setPermissionRetry((value) => value + 1);
                    else if (unresolved.length) {
                      const signal = lifetime.current?.signal;
                      void Promise.all(unresolved.map((id) => files.getById(id, { signal }).then(reference)))
                        .then((hydrated) => {
                          if (!signal?.aborted) {
                            setSelected((current) => current.map((item) => hydrated.find((file) => file.fileId === item.fileId) ?? item));
                            setUnresolved([]);
                          }
                        })
                        .catch((failure: unknown) => {
                          if (!signal?.aborted) setError(failure instanceof Error ? failure.message : "Selection could not be loaded.");
                        });
                    } else setRetry((value) => value + 1);
                  }}
                >
                  Retry
                </Button>
              }
            >
              {error}
            </Alert>
          )}
          {!capabilities && !error && <CircularProgress aria-label="Loading file permissions" />}
          {capabilities && !canSelect && !canUpload && <Alert severity="info">Files are not available with this view's permissions.</Alert>}
          {canUpload && (!publicOnly || canPublish) && (
            <Box>
              {canPublish && <FormControlLabel control={<Checkbox checked={publishPublicly} disabled={busy} onChange={(_, checked) => setPublishPublicly(checked)} />}
                label="Make the next upload public (anyone with the link can access it)" />}
              <Button variant="outlined" disabled={busy || (publicOnly && !publishPublicly) || (multiple && selected.length >= limit)} onClick={() => input.current?.click()}>
                Upload files
              </Button>
              <input
                ref={input}
                aria-label="Upload files"
                type="file"
                hidden
                multiple={multiple}
                accept={(accept.length ? accept : capabilities?.acceptedContentTypes)?.join(",")}
                onChange={(event) => {
                  const chosen = Array.from(event.target.files ?? []);
                  event.target.value = "";
                  void upload(chosen);
                }}
              />
              <Typography variant="caption" display="block">
                Maximum {Math.min(capabilities!.maximumFileBytes, maximumFileBytes ?? capabilities!.maximumFileBytes) / 1024 / 1024} MB per file
              </Typography>
            </Box>
          )}
          {uploading && (
            <Stack direction="row" spacing={1}>
              <CircularProgress size={20} />
              <Typography>Uploading {uploading}…</Typography>
            </Stack>
          )}
          {selected.length > limit && <Alert severity="warning">Select at most {limit} files. Remove extra selections explicitly.</Alert>}
          {selected.length > 0 && (
            <Box>
              <Typography variant="subtitle2">Selected files ({selected.length})</Typography>
              <List dense>
                {selected.map((file) => (
                  <ListItem
                    key={file.fileId}
                    secondaryAction={
                      <Stack direction="row">
                        {capabilities?.canSelect && (file.contentType?.startsWith("image/") || file.contentType === "application/pdf") && (
                          <Button disabled={busy} onClick={() => void showPreview(file)}>
                            Preview
                          </Button>
                        )}
                        <Button disabled={busy} onClick={() => toggle(file)}>
                          Remove
                        </Button>
                      </Stack>
                    }
                  >
                    <ListItemText primary={file.name} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
          {canSelect && (
            <Box>
              <Typography variant="subtitle2">Your files</Typography>
              {capabilities?.canFilterPublic && !publicOnly && (
                <FormControlLabel control={<Checkbox checked={showPublicOnly} onChange={(_, checked) => { setPage(0); setItems([]); setHasNext(false); setShowPublicOnly(checked); }} />}
                  label="Public files only" />
              )}
              {publicOnly && !capabilities?.canFilterPublic && <Alert severity="info">Public file search is unavailable here.</Alert>}
              {loading ? (
                <CircularProgress aria-label="Loading files" />
              ) : (
                <List>
                  {items.map((file) => {
                    const checked = selected.some((item) => item.fileId === file.fileId);
                    return (
                      <ListItem key={file.fileId} disablePadding>
                        <ListItemButton disabled={busy || !permitted(file) || (!checked && multiple && selected.length >= limit)} onClick={() => toggle(file)}>
                          <Checkbox checked={checked} tabIndex={-1} disableRipple inputProps={{ "aria-label": `Select ${file.name}` }} />
                          <ListItemText primary={file.name} secondary={file.contentType} />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                  {!items.length && <Typography color="text.secondary">No files on this page.</Typography>}
                </List>
              )}
              <Stack direction="row" spacing={1} alignItems="center">
                <Button disabled={page === 0 || loading || busy} onClick={() => setPage(page - 1)}>
                  Previous
                </Button>
                <Typography>Page {page + 1}</Typography>
                <Button disabled={!hasNext || loading || busy} onClick={() => setPage(page + 1)}>
                  Next
                </Button>
              </Stack>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!capabilities || busy || unresolved.length > 0 || selected.length > limit || (!canSelect && !canUpload)}
          onClick={() => void confirm()}
        >
          Use selected files
        </Button>
      </DialogActions>
      <Dialog open={open && !!preview} onClose={() => setPreview(undefined)} fullWidth maxWidth="md">
        <DialogTitle>
          {preview?.name}
          <IconButton aria-label="Close preview" onClick={() => setPreview(undefined)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {preview?.contentType?.startsWith("image/") ? (
            <Box component="img" src={preview.url} alt={preview.name} sx={{ maxWidth: "100%", maxHeight: "70vh" }} />
          ) : preview?.contentType === "application/pdf" ? (
            <Box component="iframe" sandbox="" src={preview.url} title={preview.name} sx={{ width: "100%", height: "70vh", border: 0 }} />
          ) : null}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
